import React, { useState } from 'react';
import { Button, Input, Label, Link, Text, useId } from '@fluentui/react-components';
import { Checkbox, mergeStyleSets } from '@fluentui/react';
import { sharedStyles, styles as WelcomeStyles } from './index';
import { useTranslation } from 'react-i18next';
import { _dataProvider } from '../../dataProvider/DataProvider';
import useRequestButton from './hooks/useRequestButton';

export type ILoginProps = {
  onRegisterSuccess?: () => void;
  onBackToLogin?: () => void;
};
const Register: React.FC<ILoginProps> = (props) => {
  const { onBackToLogin, onRegisterSuccess } = props;
  const { t } = useTranslation();
  const inputDisplayNameId = useId('displayId');
  const inputEmailId = useId('email');
  const inputPasswordId = useId('password');
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const { loadingState, setLoadingState, buttonIcon } = useRequestButton();

  const handleAcceptedTerms = (event: any) => {
    setAcceptedTerms(event.target.checked);
  };

  const handleSubmit = async (evt: React.FormEvent) => {
    if (loadingState !== 'initial') {
      return;
    }

    evt.preventDefault();
    const form = evt.target as HTMLFormElement;
    const formData = Object.fromEntries(new FormData(form).entries());

    console.log('Register Form Data:', formData);
    const { displayName, email, password } = formData as Record<'displayName' | 'email' | 'password', string>;

    setLoadingState('loading');
    const isSuccess = await signMeUp({
      displayName,
      email,
      password
    });
    if (isSuccess) {
      setTimeout(() => {
        onRegisterSuccess?.();
      }, 500);
    }
    setLoadingState(isSuccess ? 'loaded' : 'initial');
  };

  const signMeUp = async (params: { displayName: string; email: string; password: string }) => {
    const { displayName, email, password } = params;

    try {
      const response = await _dataProvider.signUp(displayName, email, password);
      if (response?.success) {
        return true;
      } else {
        console.error(response.error);
      }
    } catch (error) {
      console.error(error);
    }

    return false;
  };

  return (
    <div className={sharedStyles.container}>
      <Text as='h3' block className={WelcomeStyles.subTitle}>
        {t('welcome.register.title')}
      </Text>

      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles['form-item']}>
          <Label htmlFor={inputDisplayNameId}>
            {t('userName')}
            <Text as='h6' block>
              ({t('welcome.register.titleDescription')})
            </Text>
          </Label>
          <Input
            name='displayName'
            className={styles['form-ctrl']}
            size='large'
            id={inputDisplayNameId}
            required
            placeholder={t('welcome.placeholder.displayName')}
          />
        </div>
        <div className={styles['form-item']}>
          <Label htmlFor={inputEmailId}>
            {t('Email')}
            <Text as='h6' block>
              ({t('welcome.register.emailDescription')})
            </Text>
          </Label>
          <Input
            name='email'
            type='email'
            className={styles['form-ctrl']}
            size='large'
            id={inputEmailId}
            required
            placeholder={t('welcome.placeholder.email')}
          />
        </div>
        <div className={styles['form-item']}>
          <Label htmlFor={inputPasswordId}>{t('pwd')}</Label>
          <Input
            name='password'
            className={styles['form-ctrl']}
            size='large'
            type='password'
            id={inputPasswordId}
            required
          />
        </div>
        <div className={styles['form-item']}>
          <Label className={styles.agreeToTerms}>
            <Checkbox name='agreeToTerms' checked={acceptedTerms} onChange={handleAcceptedTerms} />
            {t('IAgreeToThe')}
            <Link href='/#/AboutUs#au' target='_blank' style={{ color: '#FF9900' }}>
              {t('TermsOfService')}
            </Link>
            <Text>{t('welcome.register.termsConjunction')}</Text>
            <Link href='/#/AboutUs#tm' target='_blank' style={{ color: '#FF9900' }}>
              {t('PrivacyPolicy')}
            </Link>
          </Label>
        </div>
        <div className={styles['form-item']}>
          <Button
            className={styles.primaryButton}
            type='submit'
            appearance='secondary'
            icon={buttonIcon}
            size='large'
            disabled={!acceptedTerms}>
            {t('submit')}
          </Button>
          <Link style={{ color: '#FF9900', textAlign: 'center' }} onClick={onBackToLogin}>
            {t('welcome.backToLogin')}
          </Link>
        </div>
      </form>
    </div>
  );
};

export default Register;

const styles = mergeStyleSets({
  description: {
    whiteSpace: 'nowrap !important'
  },

  form: {},

  ['form-item']: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
    gap: '4px',
    marginTop: '24px',
    alignItems: 'center',
    '& label': {
      fontWeight: 'bold'
    },

    '& label h6': {
      fontSize: '12px',
      whiteSpace: 'nowrap'
    }
  },

  ['form-ctrl']: {
    width: '100%'
  },

  agreeToTerms: {
    fontWeight: 'normal !important',
    display: 'flex',
    width: '100%',
    justifyContent: 'center'
  },

  primaryButton: {
    marginTop: '48px !important',
    width: '192px'
  }
});
